import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, object, safeParse, string } from "valibot";
import { dataName, fileType, gridFSFile, label, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { EXISTS_CONDITION_KEYS, MAX_CHARACTERS_XL } from "../../constants";
import { useFormServices } from "../../services/formServices";
import MixedInput from "../../components/inputs/MixedInput";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import type { FieldType } from "../../types";
import {
  parseStringTemplate,
  parseStringTemplateForField,
  stringTemplateToString,
} from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import FileDownloadButton from "../valueRendering/FileDownloadButton";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { SingleSelectInput } from "../../components/menu/Select";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import { createMemo } from "solid-js";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";

export const FILE_NAME_FIELDS = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
] as const satisfies FieldType[];

export const FILE_NAME_VARIABLES = [
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
] as const satisfies VariableType[];

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  allowed_file_type: string(),
  required: boolean(),
  filename: string(),
});
const DomainSchema = object({
  label_text: label(),
  data_name: dataName(),
  allowed_file_type: fallback(fileType(), "documents"),
  required: required(),
  filename: stringTemplate(FILE_NAME_FIELDS, FILE_NAME_VARIABLES, [], MAX_CHARACTERS_XL, false),
});
const ValueSchema = gridFSFile();

export type FileFieldProperties = InferOutput<typeof DomainSchema>;
type FileFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const FileField: Field<FileFieldProperties, FileFieldDtoType, ValueType> = {
  type: "file",
  category: "media",
  widget: "com.moreapps.plugins:file:7",
  schema: DomainSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    allowed_file_type: "documents",
    required: false,
    filename: [],
  }),
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    filename: properties.filename.map(updateReference),
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => value,
  renderValue: (value) => <FileDownloadButton fileId={value} />,
  icon: "documentText",
  titleKey: keyFromSelector(($) => $.FILE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.FILE_FIELD_DESCRIPTION),
  propertyKeys: {
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    allowed_file_type: keyFromSelector(($) => $.FIELD_PROPERTY_ALLOWED_FILE_TYPE),
    filename: keyFromSelector(($) => $.FIELD_PROPERTY_FILE_NAME),
  },
  toDto: (domain, fields, variables) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      filename: stringTemplateToString(domain.filename, fields, variables),
    });
    return output as FileFieldDtoType;
  },
  postProcessFromDto: (domain, fields, variables, scope) => ({
    ...domain,
    filename: parseStringTemplateForField(
      domain.filename,
      fields.filter((field) => field.scope === scope),
      variables,
    ),
  }),
  PreviewContent: (props) => {
    const { t } = useTranslation();
    const buttonText = createMemo(() => {
      switch (props.properties.allowed_file_type) {
        case "audio":
          return t(($) => $.FILE_FIELD_PREVIEW_ADD_BUTTON_LABEL_AUDIO);
        default:
        case "documents":
          return t(($) => $.FILE_FIELD_PREVIEW_ADD_BUTTON_LABEL_DOCUMENTS);
        case "images":
          return t(($) => $.FILE_FIELD_PREVIEW_ADD_BUTTON_LABEL_IMAGES);
        case "video":
          return t(($) => $.FILE_FIELD_PREVIEW_ADD_BUTTON_LABEL_VIDEO);
      }
    });

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <FieldButtonPreview icon="paperClip" text={buttonText()} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => {
              fieldService.setLabelProperty(props.uid, e.target.value);
            }}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_FILE)}>
          <SingleSelectInput
            label={t(props.propertyKeys.allowed_file_type)}
            value={props.data.properties.allowed_file_type}
            errorMessage={props.getError("allowed_file_type")}
            options={[
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_FILE_DOCUMENT), value: "documents" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_FILE_IMAGE), value: "images" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_FILE_AUDIO), value: "audio" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_FILE_VIDEO), value: "video" },
            ]}
            onSelect={(value) => props.setValue("allowed_file_type", value)}
            sameWidth
          />
          <MixedInput
            label={t(props.propertyKeys.filename)}
            value={props.data.properties.filename}
            parse={(value) =>
              parseStringTemplate(
                value,
                fieldService.getFieldsByTypes(FILE_NAME_FIELDS),
                variableService.getVariablesByTypes(FILE_NAME_VARIABLES),
              )
            }
            errorMessage={props.getError("filename")}
            onChange={(value) => {
              props.setValue("filename", value);
            }}
            info={{
              title: t(props.propertyKeys.filename),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
              description: t(($) => $.FIELD_PROPERTY_FILE_NAME_DESCRIPTION),
            }}
            options={createDynamicValueOptions(t, {
              fields: fieldService.getFieldsByTypes(FILE_NAME_FIELDS).flatMap((field) => toFieldValueOptions(field)),
              subforms: [],
              customVariables: variableService.getVariablesByTypes(FILE_NAME_VARIABLES).map(toVariableValueOption),
              metaVariables: [],
            })}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(FileField);
